import AccountIcon from '@components/atoms/icons/settings/account';
import ChannelsChoiceIcon from '@components/atoms/icons/settings/channelsChoice';
import DiskIcon from '@components/atoms/icons/settings/disk';
import Features from '@shared/types/enums/features';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import LanguageIcon from '@components/atoms/icons/settings/language';
import LogoutIcon from '@components/atoms/icons/settings/logout';
import ManageChannelsIcon from '@components/atoms/icons/settings/manageChannels';
import Modals from '@shared/types/enums/modals';
import Pages from '@pages';
import PlanUtils from '@shared/utils/plan';
import PowerSavingIcon from '@components/atoms/icons/settings/powerSaving';
import Setting from '@shared/classes/setting';
import SupportIcon from '@components/atoms/icons/settings/support';
import User from '@shared/classes/user';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { pushRoute } from '@shared/store/thunks/route.thunk';
import { setFocus } from '@shared/store/slices/focus.slice';
import { setModal } from '@shared/store/slices/modal.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Hook to use the settings page.
 * Handles the settings page logic.
 */
export default function useSettings() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { planId } = useAppSelector(userSelector);
  const { isAnonymous } = useAppSelector(userSelector);

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.MOSAIC));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const plan = User.getPlan(planId);
    const canOrderChannels = PlanUtils.checkFeature(Features.ORDER_CHANNELS);
    const settings = [
      new Setting({
        title: 'PAGES.SETTINGS.ACCOUNT.CARD',
        Icon: AccountIcon,
        action: () => {
          dispatch(
            pushRoute({
              route: { Page: Pages.ACCOUNT },
              params: { stack: { index: 0 } },
            })
          );
        },
      }),
      new Setting({
        title: 'PAGES.SETTINGS.DISK.CARD',
        Icon: DiskIcon,
        action: () => {
          if (isAnonymous) {
            dispatch(setModal(Modals.ANONYMOUS_GENERIC));
          } else {
            dispatch(
              pushRoute({
                route: { Page: Pages.DISK },
                params: { stack: { index: 1 } },
              })
            );
          }
        },
      }),
    ];

    if (plan?.features.includes(Features.POWER_SAVING)) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.POWER_SAVING.TITLE',
          Icon: PowerSavingIcon,
          action: () => {
            dispatch(
              pushRoute({
                route: { Page: Pages.POWER_SAVING },
                params: { stack: { index } },
              })
            );
          },
        })
      );
    }

    if (isAnonymous || canOrderChannels) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.MANAGE_CHANNELS.CARD',
          Icon: ManageChannelsIcon,
          action: () => {
            if (isAnonymous) {
              dispatch(setModal(Modals.ANONYMOUS_GENERIC));
            } else {
              dispatch(
                pushRoute({
                  route: { Page: Pages.MANAGE_CHANNELS },
                  params: { stack: { index } },
                })
              );
            }
          },
        })
      );
    }

    if (FlavourConfig.languages.length > 1) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.LANGUAGES.CARD',
          Icon: LanguageIcon,
          action: () => {
            dispatch(
              pushRoute({
                route: { Page: Pages.SETTING_LANGUAGES },
                params: { stack: { index } },
              })
            );
          },
        })
      );
    }

    const supportIndex = settings?.length || 0;
    settings.push(
      new Setting({
        title: 'PAGES.SETTINGS.SUPPORT.CARD',
        Icon: SupportIcon,
        action: () => {
          dispatch(
            pushRoute({
              route: { Page: Pages.SUPPORT },
              params: { stack: { index: supportIndex } },
            })
          );
        },
      })
    );

    const channelsChoiceIndex = settings?.length || 0;
    settings.push(
      new Setting({
        title: 'PAGES.SETTINGS.CHANNELS_CHOICE.CARD',
        Icon: ChannelsChoiceIcon,
        action: () => {
          dispatch(
            pushRoute({
              route: {
                Page: Pages.CHANNELS_CHOICE,
                push: true,
              },
              params: {
                current: { fromSettings: true },
                stack: { index: channelsChoiceIndex },
              },
            })
          );
          dispatch(setFocus(Focus.CHANNELS_CHOICE));
        },
      })
    );

    if (!isAnonymous && settings.length) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.LOGOUT.CARD',
          Icon: LogoutIcon,
          action: () => {
            dispatch(
              pushRoute({
                route: { Page: Pages.LOGOUT },
                params: { stack: { index } },
              })
            );
          },
        })
      );
    }

    return settings;
  }, []);
}
